import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import GroupAddRoundedIcon from '@mui/icons-material/GroupAddRounded'
import { useAuth } from '../../context/useAuth'
import { useDisplayName } from '../../hooks/useDisplayName'
import { useWorkspace } from '../../context/useWorkspace'
import { isNoSignInPreviewActive } from '../../debug'
import { firebaseAuth } from '../../services/firebase'
import {
  claimPendingInvite,
  declinePendingInvite,
  isInviteExpired,
  subscribeMemberInvitesForUser,
} from '../../services/inviteService'
import { type PendingInvite, isWebPendingInviteRole } from '../../types/invites'
import { Button } from '../Common/Button'
import { Loader } from '../Common/Loader'
import { useNls } from '../../nls/nlsProvider'
import { formatRoleLabel } from '../../utils/roleLabels'
import { displayNameOrMaskedPhone } from '../../utils/phoneNumber'
import type { NlsKey } from '../../nls/strings_nls'

export type PendingInviteInboxProps = {
  roleHint?: 'MEMBER'
  compact?: boolean
  hideWhenEmpty?: boolean
  onPendingCountChange?: (count: number) => void
  onAccepted?: (invite: PendingInvite) => void
  invites?: PendingInvite[]
  loading?: boolean
  error?: string | null
  onRetry?: () => void
}

const inviteSubtitle = (invite: PendingInvite, t: (key: NlsKey) => string): string => {
  const method = invite.type === 'PHONE'
    ? displayNameOrMaskedPhone(null, invite.recipient)
    : invite.type === 'EMAIL'
      ? invite.recipient
      : 'Shared link'
  return `${formatRoleLabel(t, invite.role)} invite · ${method}`
}

export const PendingInviteInbox = ({
  roleHint = 'MEMBER',
  compact = false,
  hideWhenEmpty = false,
  onPendingCountChange,
  onAccepted,
  invites: propInvites,
  loading: propLoading,
  error: propError,
  onRetry,
}: PendingInviteInboxProps) => {  const { t } = useNls()
  const tRef = useRef(t)
  useEffect(() => {
    tRef.current = t
  }, [t])
  const { currentUser, userProfile } = useAuth()
  const displayName = useDisplayName(userProfile ?? currentUser)
  const { refreshWorkspaces } = useWorkspace()

  const userEmail = currentUser?.email ?? ''

  const [internalState, setInternalState] = useState<{
    email: string
    invites: PendingInvite[]
    loading: boolean
    error: string | null
  }>(() => ({
    email: userEmail,
    invites: [],
    loading: Boolean(userEmail && !isNoSignInPreviewActive()),
    error: null,
  }))
  const [retryKey, setRetryKey] = useState(0)

  const [busyId, setBusyId] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const isControlled = propInvites !== undefined
  const isMatchingUser = internalState.email === userEmail
  const internalInvites = isMatchingUser ? internalState.invites : []
  const internalLoading = isMatchingUser ? internalState.loading : Boolean(userEmail && !isNoSignInPreviewActive())
  const internalError = isMatchingUser ? internalState.error : null

  const activeInvites = isControlled ? propInvites : internalInvites
  const loading = isControlled ? (propLoading ?? false) : internalLoading
  const queryError = isControlled ? (propError ?? null) : internalError
  const error = actionError || queryError

  const claimingUser = useMemo(() => ({
    uid: currentUser?.uid ?? '',
    email: currentUser?.email ?? null,
    phoneE164: currentUser?.phoneNumber ?? null,
    displayName: displayName || currentUser?.email || currentUser?.phoneNumber || null,
  }), [currentUser, displayName])

  const onPendingCountChangeRef = useRef(onPendingCountChange)
  useEffect(() => {
    onPendingCountChangeRef.current = onPendingCountChange
  }, [onPendingCountChange])

  useEffect(() => {
    if (roleHint !== 'MEMBER') {
      console.error('[PendingInviteInbox] Web surface received non-MEMBER roleHint. This is a regression.', roleHint)
    }
  }, [roleHint])

  const handleRetry = useCallback(() => {
    setActionError(null)
    if (onRetry) {
      onRetry()
    } else {
      setRetryKey((k) => k + 1)
    }
  }, [onRetry])

  useEffect(() => {
    if (isControlled) return
    if (!userEmail || isNoSignInPreviewActive()) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setInternalState({
        email: '',
        invites: [],
        loading: false,
        error: null,
      })
      onPendingCountChangeRef.current?.(0)
      return
    }

    let isCurrent = true
    setInternalState({
      email: userEmail,
      invites: [],
      loading: true,
      error: null,
    })

    const unsub = subscribeMemberInvitesForUser(
      userEmail,
      (nextInvites) => {
        if (!isCurrent) return
        setInternalState({
          email: userEmail,
          invites: nextInvites,
          loading: false,
          error: null,
        })
        onPendingCountChangeRef.current?.(nextInvites.length)
      },
      (err) => {
        if (!isCurrent) return
        setInternalState({
          email: userEmail,
          invites: [],
          loading: false,
          error: err.message || tRef.current('NLSPendingInvitesLoadError'),
        })
        onPendingCountChangeRef.current?.(0)
      },
    )

    return () => {
      isCurrent = false
      if (typeof unsub === 'function') unsub()
    }
  }, [userEmail, isControlled, retryKey])

  const accept = async (invite: PendingInvite) => {
    setBusyId(invite.id)
    setActionError(null)
    setMessage(null)
    try {
      await claimPendingInvite(invite.id, claimingUser)
      await firebaseAuth.currentUser?.getIdToken(true)
      await refreshWorkspaces()
      if (!isControlled) {
        setInternalState((prev) => {
          const next = prev.invites.filter((entry) => entry.id !== invite.id)
          onPendingCountChange?.(next.length)
          return { ...prev, invites: next }
        })
      }
      setMessage(`Accepted invite to ${invite.workspaceName || 'workspace'}.`)
      onAccepted?.(invite)
    } catch (caught) {
      setActionError(caught instanceof Error ? caught.message : t('NLSPendingInvitesAcceptError'))
    } finally {
      setBusyId(null)
    }
  }

  const decline = async (invite: PendingInvite) => {
    if (invite.type === 'LINK') return
    setBusyId(invite.id)
    setActionError(null)
    setMessage(null)
    try {
      await declinePendingInvite(invite.id, claimingUser)
      if (!isControlled) {
        setInternalState((prev) => {
          const next = prev.invites.filter((entry) => entry.id !== invite.id)
          onPendingCountChange?.(next.length)
          return { ...prev, invites: next }
        })
      }
      setMessage('Invite declined.')
    } catch (caught) {
      setActionError(caught instanceof Error ? caught.message : t('NLSPendingInvitesDeclineError'))
    } finally {
      setBusyId(null)
    }
  }

  const displayInvites = activeInvites.filter((invite) => {
    if (invite.role === 'HELPER' || !isWebPendingInviteRole(invite.role)) {
      console.warn('[PendingInviteInbox] Filtered out non-MEMBER invite on web surface:', invite.id, invite.role)
      return false
    }
    return !isInviteExpired(invite)
  })

  if (hideWhenEmpty && (loading || (!error && !message && displayInvites.length === 0))) return null

  return (
    <section className={`rounded-3xl border border-[var(--border)] bg-[var(--surface-raised)] ${compact ? 'p-4' : 'p-5'} text-[var(--text-strong)]`} data-testid="pending-invite-inbox">
      <header className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--accent-primary)]">Invitations</p>
          <h2 className={`${compact ? 'text-base' : 'text-lg'} font-semibold text-[var(--text-strong)]`}>Pending for you</h2>
        </div>
        <GroupAddRoundedIcon className="text-[var(--accent-primary)]" />
      </header>

      {error ? (
        <div className="mt-3 flex items-center justify-between rounded-2xl border border-[color-mix(in_srgb,var(--danger)_30%,transparent)] bg-[color-mix(in_srgb,var(--danger)_10%,transparent)] p-3 text-xs text-[color-mix(in_srgb,var(--danger)_60%,var(--text-strong))]">
          <span>{error}</span>
          <Button
            variant="tertiary"
            size="sm"
            tone="danger"
            className="ml-2"
            onClick={handleRetry}
          >
            {t('NLSPendingInvitesRetry')}
          </Button>
        </div>
      ) : null}

      {message ? <p className="mt-3 rounded-2xl border border-[color-mix(in_srgb,var(--success)_30%,transparent)] bg-[color-mix(in_srgb,var(--success)_10%,transparent)] p-3 text-xs text-[color-mix(in_srgb,var(--success)_60%,var(--text-strong))]">{message}</p> : null}

      <div className="mt-4 grid gap-2">
        {loading ? (
          <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-4 text-sm text-[var(--muted)]">
            <Loader size={140} label="Checking invites" />
          </div>
        ) : error ? null : displayInvites.length === 0 ? (
          <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-4 text-sm text-[var(--muted)]">
            No pending member invites for this account.
          </div>

        ) : displayInvites.map((invite) => {
          if (!isWebPendingInviteRole(invite.role)) {
            if (import.meta.env.DEV && import.meta.env.MODE !== 'test') {
              throw new Error(`[PendingInviteInbox] Non-MEMBER invite reached render: ${invite.id} (${invite.role})`)
            }
            console.warn('[PendingInviteInbox] Non-MEMBER invite reached render:', invite.id, invite.role)
            return null
          }
          return (
            <article key={invite.id} className="rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="truncate text-sm font-semibold text-[var(--text-strong)]">{invite.workspaceName || 'Workspace'}</h3>
                  <p className="mt-1 text-xs text-[var(--muted)]">{inviteSubtitle(invite, t)}</p>
                  {invite.invitedByName ? <p className="mt-1 text-[11px] text-[var(--muted)]">From {invite.invitedByName}</p> : null}
                </div>
                <span className="rounded-full border border-[color-mix(in_srgb,var(--warning)_30%,transparent)] bg-[color-mix(in_srgb,var(--warning)_10%,transparent)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-[color-mix(in_srgb,var(--warning)_60%,var(--text-strong))]">Pending</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  variant="primary"
                  size="md"
                  disabled={busyId !== null}
                  onClick={() => void accept(invite)}
                >
                  {busyId === invite.id ? 'Accepting...' : 'Accept'}
                </Button>
                {invite.type !== 'LINK' ? (
                  <Button
                    variant="tertiary"
                    size="md"
                    disabled={busyId !== null}
                    onClick={() => void decline(invite)}
                  >
                    {busyId === invite.id ? 'Working...' : 'Decline'}
                  </Button>
                ) : null}
              </div>
            </article>
          )
        })}
      </div>
    </section>
  )
}
